// ---------------------------------------------------------------------------
// 損益分岐点分析 — 部門 (section_id) → グループ → 店舗 の固定グルーピング定義 (2026-06-25)
//   会社=クマガイコーポレーション (freee company_id=11518223)。
//   仕様 (ユーザー承認済):
//     そば       = 信州屋渋谷 + 信州屋新宿南口 + 喜乃字屋
//     レストラン  = La SOFFITTA + Bistrot Gourmand + chiocciol@
//     カフェ      = 人間関係 + BiOcafe
//     和食       = 備中屋 + 山城屋(=庄藏 + 分店 を 1 店舗合算)  ※山城屋EC は既定除外
//     店舗(合計)  = そば + レストラン + カフェ + 和食
//     非店舗     = 本部 / ワイン販売課 / VinoBuono / 煙草販売 / デリバリー / その他 (個別表示)
//                   ※ワイン販売課と VinoBuono は分けて表示
//     全社       = 全 available section の合算 (= 店舗合計 + 非店舗。EC 除外)
//   worker 側 /api/breakeven/report の group キーと 1:1 対応させる。
// ---------------------------------------------------------------------------

export const SECTION = {
  本部: 3068540,
  LaSOFFITTA: 3068544,
  人間関係: 3068545,
  信州屋渋谷: 3068546,
  信州屋新宿南口: 3068547,
  BiOcafe: 3068548,
  山城屋庄藏: 3068549,
  山城屋分店: 3068550,
  山城屋EC: 3095534,
  chiocciola: 3068551,
  喜乃字屋: 3068552,
  BistrotGourmand: 3068553,
  備中屋: 3068554,
  ワイン販売課: 3068542,
  VinoBuono: 3068541,
  煙草販売: 3068543,
  デリバリー: 3095535,
  その他: 3068555,
} as const;

/** 山城屋(庄藏+分店)の仮想合算 section。負の sentinel id で freee section と衝突回避 (BudgetActualPage 踏襲)。 */
export const YAMASHIROYA_COMBINED = -549550;

export interface StoreDef {
  /** worker group キーと一致 (= store:<sid> 形式 or 仮想店舗名)。 */
  key: string;
  name: string;
  /** 単一 section。combine がある場合は代表 section / 仮想 sentinel。 */
  sectionId: number;
  /** 複数 section の合算 (山城屋=庄藏+分店)。 */
  combine?: number[];
}

export interface GroupDef {
  /** worker group キー (company / stores / soba ...)。 */
  key: string;
  label: string;
  /** このグループに含まれる全 section_id (集計検算用)。 */
  sectionIds: number[];
  /** ドリル時に展開する配下店舗。 */
  stores: StoreDef[];
  /** 経営管理上の分類 (UI のセクション分け用)。 */
  kind: 'aggregate' | 'restaurant_group' | 'non_store';
}

// --- 店舗グループ (飲食店) ---
const SOBA: GroupDef = {
  key: 'soba', label: 'そば', kind: 'restaurant_group',
  sectionIds: [SECTION.信州屋渋谷, SECTION.信州屋新宿南口, SECTION.喜乃字屋],
  stores: [
    { key: `store:${SECTION.信州屋渋谷}`, name: '信州屋 渋谷店', sectionId: SECTION.信州屋渋谷 },
    { key: `store:${SECTION.信州屋新宿南口}`, name: '信州屋 新宿南口店', sectionId: SECTION.信州屋新宿南口 },
    { key: `store:${SECTION.喜乃字屋}`, name: '喜乃字屋', sectionId: SECTION.喜乃字屋 },
  ],
};

const RESTAURANT: GroupDef = {
  key: 'restaurant', label: 'レストラン', kind: 'restaurant_group',
  sectionIds: [SECTION.LaSOFFITTA, SECTION.BistrotGourmand, SECTION.chiocciola],
  stores: [
    { key: `store:${SECTION.LaSOFFITTA}`, name: 'La SOFFITTA', sectionId: SECTION.LaSOFFITTA },
    { key: `store:${SECTION.BistrotGourmand}`, name: 'Bistrot Gourmand', sectionId: SECTION.BistrotGourmand },
    { key: `store:${SECTION.chiocciola}`, name: 'chiocciol@pizzeria', sectionId: SECTION.chiocciola },
  ],
};

const CAFE: GroupDef = {
  key: 'cafe', label: 'カフェ', kind: 'restaurant_group',
  sectionIds: [SECTION.人間関係, SECTION.BiOcafe],
  stores: [
    { key: `store:${SECTION.人間関係}`, name: '人間関係', sectionId: SECTION.人間関係 },
    { key: `store:${SECTION.BiOcafe}`, name: 'BiOcafe', sectionId: SECTION.BiOcafe },
  ],
};

const WASHOKU: GroupDef = {
  key: 'washoku', label: '和食', kind: 'restaurant_group',
  // 山城屋EC(3095534) は既定で和食合算に含めない (要ユーザー確認項目)。
  sectionIds: [SECTION.備中屋, SECTION.山城屋庄藏, SECTION.山城屋分店],
  stores: [
    { key: `store:${SECTION.備中屋}`, name: '備中屋長左衛門', sectionId: SECTION.備中屋 },
    {
      key: `store:${YAMASHIROYA_COMBINED}`, name: '山城屋', sectionId: YAMASHIROYA_COMBINED,
      combine: [SECTION.山城屋庄藏, SECTION.山城屋分店],
    },
  ],
};

// --- 非店舗 (個別表示。ワイン販売課と VinoBuono は分ける) ---
const NON_STORE_DEFS: StoreDef[] = [
  { key: `store:${SECTION.本部}`, name: '本部', sectionId: SECTION.本部 },
  { key: `store:${SECTION.ワイン販売課}`, name: 'ワイン販売課', sectionId: SECTION.ワイン販売課 },
  { key: `store:${SECTION.VinoBuono}`, name: 'VinoBuono', sectionId: SECTION.VinoBuono },
  { key: `store:${SECTION.煙草販売}`, name: '煙草販売', sectionId: SECTION.煙草販売 },
  { key: `store:${SECTION.デリバリー}`, name: 'デリバリー', sectionId: SECTION.デリバリー },
  { key: `store:${SECTION.その他}`, name: 'その他', sectionId: SECTION.その他 },
];

const RESTAURANT_GROUPS: GroupDef[] = [SOBA, RESTAURANT, CAFE, WASHOKU];

const STORES_TOTAL: GroupDef = {
  key: 'stores', label: '店舗(合計)', kind: 'aggregate',
  sectionIds: RESTAURANT_GROUPS.flatMap((g) => g.sectionIds),
  stores: RESTAURANT_GROUPS.flatMap((g) => g.stores),
};

const COMPANY: GroupDef = {
  key: 'company', label: '全社', kind: 'aggregate',
  // 全社は worker 側 section_id 省略の単一取得が正味合計 (EC/閉店分も自動内包)。
  sectionIds: [],
  stores: [...STORES_TOTAL.stores, ...NON_STORE_DEFS],
};

const HEADQUARTERS: GroupDef = {
  key: `store:${SECTION.本部}`, label: '本部', kind: 'non_store',
  sectionIds: [SECTION.本部],
  stores: [{ key: `store:${SECTION.本部}`, name: '本部', sectionId: SECTION.本部 }],
};

/** UI のグループチップ並び順 (全社 → 本部 → 店舗合計 → 飲食 4 グループ → 非店舗個別)。 */
export const GROUP_DEFS: GroupDef[] = [
  COMPANY,
  STORES_TOTAL,
  ...RESTAURANT_GROUPS,
];

/** チップ「全社 / 本部 / 店舗合計 | そば レストラン カフェ 和食」 + 非店舗個別 のスコープ全集合。 */
export interface ScopeDef { key: string; label: string; sectionIds: number[]; stores: StoreDef[]; isStore: boolean; }

export const SCOPES: ScopeDef[] = [
  { key: COMPANY.key, label: COMPANY.label, sectionIds: COMPANY.sectionIds, stores: COMPANY.stores, isStore: false },
  { key: HEADQUARTERS.key, label: HEADQUARTERS.label, sectionIds: HEADQUARTERS.sectionIds, stores: HEADQUARTERS.stores, isStore: true },
  { key: STORES_TOTAL.key, label: STORES_TOTAL.label, sectionIds: STORES_TOTAL.sectionIds, stores: STORES_TOTAL.stores, isStore: false },
  ...RESTAURANT_GROUPS.map((g): ScopeDef => ({ key: g.key, label: g.label, sectionIds: g.sectionIds, stores: g.stores, isStore: false })),
  // 非店舗の個別 (本部以外)
  { key: `store:${SECTION.ワイン販売課}`, label: 'ワイン販売課', sectionIds: [SECTION.ワイン販売課], stores: NON_STORE_DEFS.filter((s) => s.sectionId === SECTION.ワイン販売課), isStore: true },
  { key: `store:${SECTION.VinoBuono}`, label: 'VinoBuono', sectionIds: [SECTION.VinoBuono], stores: NON_STORE_DEFS.filter((s) => s.sectionId === SECTION.VinoBuono), isStore: true },
  { key: `store:${SECTION.デリバリー}`, label: 'デリバリー', sectionIds: [SECTION.デリバリー], stores: NON_STORE_DEFS.filter((s) => s.sectionId === SECTION.デリバリー), isStore: true },
];

export function scopeByKey(key: string): ScopeDef | undefined {
  return SCOPES.find((s) => s.key === key);
}
